"use client";

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Calendar, User, ArrowLeft, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;

function SinglePostConsumer() {
  const { post_id } = useParams<{ post_id: string }>();
  const pathSegments = window.location.pathname.split("/").filter(Boolean);
  const slug = pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : undefined;

  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPost() {
      try {
        setLoading(true);
        setError(null);

        let endpoint = "/?rest_route=/wp/v2/posts&_embed=wp:featuredmedia,wp:term";
        if (post_id) {
          endpoint += `&include[]=${post_id}`;
        } else if (slug) {
          endpoint += `&slug=${encodeURIComponent(slug)}`;
        }

        const res = await fetch(endpoint);
        if (!res.ok) {
          throw new Error("Failed to fetch post");
        }

        const data = await res.json();
        if (!data || data.length === 0) {
          setPost(null);
          setLoading(false);
          return;
        }

        const raw = data[0];
        const embedded = raw._embedded || {};
        const mediaArr = embedded["wp:featuredmedia"];
        const featuredImage = mediaArr && mediaArr[0] ? mediaArr[0].source_url : "";

        
        const termGroups = embedded["wp:term"] || [];
        const categories: any[] = [];
        const tags: any[] = [];

        termGroups.forEach((group: any[]) => {
          if (Array.isArray(group)) {
            group.forEach((term: any) => {
              if (term.taxonomy === "category") {
                categories.push({ id: term.id, name: term.name, slug: term.slug, taxonomy: term.taxonomy });
              } else if (term.taxonomy === "post_tag") {
                tags.push({ id: term.id, name: term.name, slug: term.slug, taxonomy: term.taxonomy });
              }
            });
          }
        });

        
        const authorObj = raw._embedded?.["author"]?.[0] || { id: raw.author, name: "Dr. Rodolfo Carrillo-Jimenez" };

        const mappedPost = {
          id: String(raw.id),
          title: raw.title?.rendered || "",
          content: raw.content?.rendered || "",
          excerpt: raw.excerpt?.rendered || "",
          date: raw.date,
          slug: raw.slug,
          link: raw.link,
          featuredImage,
          categories,
          tags,
          author: {
            id: authorObj.id,
            name: authorObj.name || "Dr. Rodolfo Carrillo-Jimenez",
            avatar: authorObj.avatar_urls || { "96": authorObj.avatar?.["96"] }
          }
        };

        setPost(mappedPost);
      } catch (err: any) {
        setError(err.message || "Error loading post");
      } finally {
        setLoading(false);
      }
    }

    fetchPost();
  }, [post_id, slug]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="h-12 bg-slate-800/30 rounded-md animate-pulse" />
        <div className="h-96 bg-slate-800/30 rounded-md animate-pulse" />
        <div className="space-y-3">
          <div className="h-4 bg-slate-800/30 rounded-md animate-pulse" />
          <div className="h-4 bg-slate-800/30 rounded-md animate-pulse w-5/6" />
          <div className="h-4 bg-slate-800/30 rounded-md animate-pulse w-4/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-slate-400">Unable to load this article. Please try again later.</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-slate-400">Article not found.</p>
      </div>
    );
  }

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"];

  return (
    <article className="max-w-4xl mx-auto"><Link to="/blog/" className="mb-8 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" />Back to Blog</Link>

      {}
      <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-center text-white mb-6 leading-tight">
        {post.title}
      </h1>


      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-lg shadow-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      {}
      <div
        className="prose prose-lg max-w-none text-slate-300 font-default text-lg leading-relaxed [&>p]:mb-6 [&>h2]:font-default [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:text-white [&>h2]:mt-12 [&>h2]:mb-4 [&>h3]:font-default [&>h3]:text-2xl [&>h3]:font-semibold [&>h3]:text-white [&>h3]:mt-8 [&>h3]:mb-3 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>blockquote]:border-l-4 [&>blockquote]:border-red-600 [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:my-6 [&_a]:text-red-500 [&_a]:underline [&_img]:rounded-lg [&_img]:my-6"
        dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || "" }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-slate-700">
          <span className="text-sm text-slate-400 mr-2 self-center">Tags:</span>
          {post.tags.map((tag: any, i: number) => (
            <span
              data-index={i}
              key={tag.id}
              className="inline-block px-3 py-1 text-xs bg-slate-800 text-slate-300 rounded-md"
            >
              {tag.name}
            </span>
          ))}
        </div>
      )}

      </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="space-y-3">
            <div className="h-48 bg-slate-800/30 rounded-md animate-pulse" />
            <div className="h-6 bg-slate-800/30 rounded-md animate-pulse" />
            <div className="h-4 bg-slate-800/30 rounded-md animate-pulse w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <p className="text-center text-slate-400 font-default">
        No related articles available at this moment.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => {
        const formattedDate = post.date
          ? new Date(post.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";
        return (
          <Link
            key={post.id}
            to={post.link || "#"}
            data-index={i}
            className="group block"
          >
            <Card className="h-full overflow-hidden border border-slate-700 bg-slate-800 hover:shadow-lg transition-shadow duration-300">
              {post.featuredImage && (
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              )}
              <CardContent className="p-6">
                {formattedDate && (
                  <p className="text-xs uppercase tracking-widest text-slate-400 mb-3">
                    {formattedDate}
                  </p>
                )}
                <h3 className="font-default text-xl font-semibold text-white mb-3 group-hover:text-red-500 transition-colors line-clamp-2">
                  {post.title}
                </h3>
                {post.excerpt && (
                  <div
                    className="text-sm text-slate-400 font-default line-clamp-3"
                    dangerouslySetInnerHTML={{
                      __html: post.excerpt.replace(/<[^>]*>/g, "").substring(0, 140) + "...",
                    }}
                  />
                )}
                <div className="flex items-center gap-2 mt-4 text-sm font-default text-red-500">
                  <span>Read article</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}

function RelatedPostsNav() {
  const { hasPrev, hasNext, prevPage, nextPage, isRefetching } = usePosts();

  if (!hasPrev && !hasNext) return null;

  return (
    <div className="flex items-center justify-center gap-4 mt-10">
      <Button
        variant="outline"
        onClick={prevPage}
        disabled={!hasPrev || isRefetching}
        className="font-default border-slate-700 text-white hover:bg-slate-800"
      >
        <ArrowLeft className="h-4 w-4" />
        Previous
      </Button>
      <Button
        variant="outline"
        onClick={nextPage}
        disabled={!hasNext || isRefetching}
        className="font-default border-slate-700 text-white hover:bg-slate-800"
      >
        Next
        <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default function Post() {
  return (
    <section data-section-id="4274"
      id="single-post-template-section"
      className="bg-slate-950 text-white py-16 md:py-24 px-4 sm:px-6 lg:px-8"
    >
      <div className="container mx-auto">
        <SinglePostConsumer />

        <Separator className="my-20 max-w-4xl mx-auto bg-slate-800" />

        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest text-red-500 mb-4 font-default">
              Continue Reading
            </p>
            <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              Related Articles
            </h2>
            <p className="mt-4 text-slate-400 font-default text-lg max-w-2xl mx-auto">
              Explore more insights on cardiovascular health and patient care from our practice.
            </p>
          </div>

          <PostsProvider wp_query={{ post_type: "post", per_page: 3, status: ["publish"] }}>
            <RelatedPostsConsumer />
            <RelatedPostsNav />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}
